import { can, type ReconcileReportDto } from '@bravo/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, ErrorBlock, PageHeader, Spinner } from '../components/ui';
import { apiData } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { useFormat } from '../lib/format';
import { useSession } from '../lib/session';

interface ReportList {
  latest: ReconcileReportDto | null;
  history: Array<Omit<ReconcileReportDto, 'issues'>>;
}

export function ReconciliationPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const session = useSession();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const query = useQuery({
    queryKey: ['reconciliation'],
    queryFn: () => apiData<ReportList>('/admin/reconciliation'),
  });
  const canRun = session.data ? can(session.data.role, 'reconciliation.run') : false;

  async function run() {
    setBusy(true);
    try {
      await apiData('/admin/reconciliation/run', { method: 'POST', body: {} });
      toastOk(t('reconcile.done'));
      await queryClient.invalidateQueries({ queryKey: ['reconciliation'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  if (query.isLoading) return <Spinner />;
  if (query.isError || !query.data) {
    return <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} />;
  }
  const latest = query.data.latest;

  return (
    <div>
      <PageHeader
        title={t('reconcile.title')}
        subtitle={t('reconcile.subtitle')}
        actions={
          canRun ? (
            <Button data-testid="reconcile-run" loading={busy} onClick={() => void run()}>
              {t('reconcile.run')}
            </Button>
          ) : null
        }
      />
      {!latest ? <p className="text-sm text-fg-muted">{t('reconcile.none')}</p> : null}
      {latest ? (
        <Card>
          <div className="flex items-center gap-3">
            <Badge tone={latest.ok ? 'brand' : 'danger'}>
              {latest.ok ? t('reconcile.ok') : t('reconcile.problems', { count: latest.issueCount })}
            </Badge>
            <span className="text-sm text-fg-muted">{format.when(latest.ranAt)}</span>
          </div>
          {latest.issues.length > 0 ? (
            <ul className="mt-4 space-y-2 text-sm">
              {latest.issues.map((issue, index) => (
                <li key={`${issue.check}-${index}`} className="rounded-xl bg-danger/5 px-3 py-2">
                  <span className="font-semibold">{issue.check}</span> · {issue.message}
                </li>
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}
      <h2 className="mb-3 mt-8 text-lg font-semibold">{t('reconcile.history')}</h2>
      <ul className="divide-y divide-border rounded-2xl border border-border bg-card">
        {query.data.history.map((run) => (
          <li key={run.ranAt} className="flex items-center justify-between px-4 py-3 text-sm">
            <span>{format.when(run.ranAt)}</span>
            <Badge tone={run.ok ? 'brand' : 'danger'}>
              {run.ok ? t('reconcile.ok') : String(run.issueCount)}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}
